import React, { useState } from "react";
import { MoreVertical, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Reusable archive / restore / delete menu for landlord & property rows.
export function RowActions({ entity, id, archived, name, canDelete, onDone }) {
  const [confirm, setConfirm] = useState(false);
  const stop = (e) => e.stopPropagation();
  const noun = entity === "properties" ? "property" : "landlord";

  const toggleArchive = async (e) => {
    stop(e);
    try {
      await api.patch(`/${entity}/${id}/archive`, { archived: !archived });
      toast.success(archived ? "Restored" : "Archived");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${entity}/${id}`);
      toast.success(`${noun[0].toUpperCase()}${noun.slice(1)} deleted`);
      setConfirm(false);
      onDone();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div onClick={stop} className="inline-flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`${noun}-actions-${id}`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid={`${noun}-archive-${id}`} onClick={toggleArchive}>
            {archived
              ? <><ArchiveRestore className="h-4 w-4 mr-2" />Restore</>
              : <><Archive className="h-4 w-4 mr-2" />Archive</>}
          </DropdownMenuItem>
          {canDelete && (
            <DropdownMenuItem data-testid={`${noun}-delete-${id}`} className="text-[#9E382A] focus:text-[#9E382A]" onClick={(e) => { stop(e); setConfirm(true); }}>
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {noun}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {name ? <strong>{name}</strong> : `this ${noun}`}
              {entity === "properties" ? " along with all its documents, issues, statements and tenants" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${noun}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${noun}-delete-confirm`} onClick={doDelete} className="bg-[#9E382A] hover:bg-[#822e22]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
